import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CommandKeybindingHint, ScopedCommands} from '@github-ui/ui-commands'
import {CheckIcon} from '@primer/octicons-react'
import {
  AnchoredOverlay,
  Button,
  type ButtonProps,
  FormControl,
  IconButton,
  Textarea,
  TextInput,
  type TextInputProps,
} from '@primer/react'
import {clsx} from 'clsx'
import {type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import {LABELS} from './constants/labels'
import {FALLBACK_WIDTH, textValidation} from './constants/text'
import styles from './IssueFieldItem.module.css'
import textStyles from './IssueFieldTextToken.module.css'
import {renderTextWithLinks} from './utils/TextUrlRenderer'

type IssueFieldTextEditorBaseProps = {
  fieldId: string
  fieldName: string
  initialValue: string
  onCommit: (fieldId: string, value: string | null) => void
  validate?: (value: string) => string | null
  inputProps?: TextInputProps
  isDraft?: boolean
  isNumber?: boolean
  // If set to true, then the text editor should render in open state
  shouldOpen?: boolean
}

export type IssueFieldTextEditorProps = IssueFieldTextEditorBaseProps & {
  errorPresent?: boolean
  className?: string
  readonly: boolean
}

export type IssueFieldCompactTextEditorProps = IssueFieldTextEditorBaseProps & {
  readonly: boolean
  className?: string
}

export type IssueFieldTextEditorInternalProps = IssueFieldTextEditorBaseProps & {
  renderAnchor: (
    buttonProps: ButtonProps | undefined,
    ref: RefObject<HTMLButtonElement | null>,
    displayTitle: string,
  ) => React.JSX.Element
  overrideWidth?: string
  onClose?: () => void
}

const defaultValidate = (value: string) => textValidation(value)

export const IssueFieldTextEditor = ({
  initialValue,
  validate = defaultValidate,
  className,
  errorPresent = false,
  readonly,
  ...props
}: IssueFieldTextEditorProps) => {
  const isCompactViewEnabled = isFeatureEnabled('issue_fields_compact_view')

  const anchor = (
    buttonProps: ButtonProps | undefined,
    ref: RefObject<HTMLButtonElement | null>,
    displayTitle: string,
  ) => (
    <SectionHeader
      ref={ref}
      title={displayTitle}
      readonly={readonly}
      buttonProps={buttonProps}
      errorPresent={errorPresent}
    />
  )

  const sectionHeader = (
    <IssueFieldTextEditorInternal initialValue={initialValue} validate={validate} renderAnchor={anchor} {...props} />
  )
  return isCompactViewEnabled ? (
    <IssueFieldCompactTextEditor
      initialValue={initialValue}
      validate={validate}
      readonly={readonly}
      className={className}
      {...props}
    />
  ) : (
    <Section sectionHeader={sectionHeader} className={className}>
      {!initialValue ? (
        <span className={textStyles.noValueContainer}>{LABELS.setValue}</span>
      ) : (
        <span className={textStyles.issuesFieldText}>{renderTextWithLinks(initialValue)}</span>
      )}
    </Section>
  )
}

export const IssueFieldCompactTextEditor = ({
  fieldId,
  fieldName,
  initialValue,
  onCommit,
  shouldOpen = false,
  validate = defaultValidate,
  inputProps,
  readonly,
  className,
  isNumber = false,
}: IssueFieldCompactTextEditorProps) => {
  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)
  // Track whether the field has unsaved changes.
  // !! Please note that we are not currently using this state as we aim for parity with existing project fields.
  const [isUnsaved, setIsUnsaved] = useState<boolean>(false)

  const buttonRef = useRef<HTMLButtonElement>(null)
  const shouldRestoreRowFocusRef = useRef(false)

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUnsaved) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isUnsaved])

  // Input field state
  const [inputValue, setInputValue] = useState<string>(initialValue)

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const {value} = e.target
    setInputValue(value)
    if (value === initialValue) {
      setIsClean(true)
    } else {
      setIsClean(false)
    }
  }

  // Validation state
  const validationMessage = useMemo<string | null>(
    () => (isClean ? null : validate(inputValue)),
    [isClean, inputValue, validate],
  )

  const preventSubmit = useMemo<boolean>(() => !!validationMessage, [validationMessage])

  // Automatically open the editor
  const [isEditing, setIsEditing] = useState<boolean>(shouldOpen)

  const openEditor = useCallback(() => {
    // Ensure the editor starts from the latest committed value.
    if (isClean) setInputValue(initialValue)
    setIsEditing(true)
  }, [initialValue, isClean])

  const onCancel = useCallback(() => {
    // Reset to initial value
    setInputValue(initialValue)
    shouldRestoreRowFocusRef.current = true
    setIsEditing(false)
    setIsClean(true)
    setIsUnsaved(false) // Cancel explicitly discards changes
  }, [initialValue])
  const onUpdate = useCallback(() => {
    setIsEditing(false)
    setIsClean(true)
    setIsUnsaved(false)
    onCommit(fieldId, inputValue)
    shouldRestoreRowFocusRef.current = true
  }, [fieldId, onCommit, inputValue])

  useLayoutEffect(() => {
    if (!isEditing && shouldRestoreRowFocusRef.current) {
      shouldRestoreRowFocusRef.current = false
      buttonRef.current?.focus()
    }
  }, [isEditing])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
      e.stopPropagation()
      // Global shortcut logic interferes with restoring the focus to the button after submitting the form via Enter. Therefore, we handle Enter and Escape key presses manually here.
      // We specifically need this because global enter key only submits the form when the focus is on the button. We need the form to be submitted when the focus is on the text input as well.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.key === 'Enter') {
        // We need to stop this as the key event sometimes handled outside React’s normal event pipeline,
        // so the component doesn’t re-render in time, and we cannot restore the focus to the button.
        e.preventDefault()
        onUpdate()
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      } else if (e.key === 'Escape') {
        onCancel()
      }
    },
    [onUpdate, onCancel],
  )

  // Handle blur to close editor when clicking outside
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      // Check if the new focus target is outside the form control
      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
        if (isClean) {
          // No changes made, just close
          setIsEditing(false)
        }
        // If changes were made, keep the editor open
      }
    },
    [isClean],
  )

  return (
    <>
      {isEditing && !readonly ? (
        <div onBlur={handleBlur}>
          <FormControl
            className={clsx(
              styles.issueFieldFormWrapper,
              styles.issueFieldFormWrapperClickable,
              styles.isEditing,
              className,
            )}
          >
            <FormControl.Label className={styles.issueFieldLabel}>{fieldName}</FormControl.Label>
            {isNumber ? (
              <TextInput
                value={inputValue}
                placeholder={LABELS.textEditorPlaceholder}
                onChange={handleTextChange}
                size="small"
                onKeyDown={onKeyDown}
                className={textStyles.issueFieldTextInput}
                autoFocus
                onFocus={event => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
                {...inputProps}
              />
            ) : (
              <Textarea
                value={inputValue}
                resize="none"
                placeholder={LABELS.textEditorPlaceholder}
                onChange={handleTextChange}
                onKeyDown={onKeyDown}
                className={clsx(textStyles.issueFieldTextInput, textStyles.issueFieldTextTextarea)}
                autoFocus
                onFocus={event => event.target.setSelectionRange(event.target.value.length, event.target.value.length)}
              />
            )}
            <IconButton
              icon={CheckIcon}
              onClick={preventSubmit ? undefined : onUpdate}
              size="small"
              aria-disabled={preventSubmit}
              inactive={preventSubmit}
              aria-label={LABELS.textEditorUpdateButton}
              className={styles.issueFieldActionButton}
            />
            {validationMessage && (
              <FormControl.Validation variant="error" className={textStyles.issuesFieldTextError}>
                {validationMessage}
              </FormControl.Validation>
            )}
          </FormControl>
        </div>
      ) : readonly ? (
        <div className={clsx(styles.issueFieldFormWrapper, className)}>
          <p className={styles.issueFieldLabel}>{fieldName}</p>
          <div className={clsx(styles.issueFieldValueText, initialValue && styles.hasValue)}>
            {initialValue ? renderTextWithLinks(initialValue) : LABELS.setValue}
          </div>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          className={clsx(styles.issueFieldFormWrapper, styles.issueFieldFormWrapperClickable, className)}
          onClick={openEditor}
          aria-label={initialValue ? `Edit ${fieldName}` : `Set ${fieldName}`}
        >
          <span className={styles.issueFieldLabel}>{fieldName}</span>
          <span className={clsx(styles.issueFieldValueText, initialValue && styles.hasValue)}>
            {initialValue ? renderTextWithLinks(initialValue) : LABELS.setValue}
          </span>
        </button>
      )}
    </>
  )
}

export const IssueFieldTextEditorInternal = ({
  fieldId,
  fieldName,
  initialValue,
  onCommit,
  shouldOpen = false,
  validate = defaultValidate,
  inputProps,
  isDraft = false,
  isNumber = false,
  renderAnchor,
  overrideWidth,
  onClose: onClosed,
}: IssueFieldTextEditorInternalProps) => {
  // Text editor is clean while the user hasn't made any changes,
  // prevent navigating away if there are unsaved changes
  const [isClean, setIsClean] = useState<boolean>(true)
  // Track whether the field has unsaved changes
  const [isUnsaved, setIsUnsaved] = useState<boolean>(false)

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isUnsaved) {
        event.preventDefault()
        event.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isUnsaved])

  // Input field state
  const [inputValue, setInputValue] = useState<string>(initialValue)

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target
    setInputValue(value)
    if (value === initialValue) {
      setIsClean(true)
    } else {
      setIsClean(false)
    }
  }

  // Validation state
  const validationMessage = useMemo<string | null>(
    () => (isClean ? null : validate(inputValue)),
    [isClean, inputValue, validate],
  )
  const preventSubmit = useMemo<boolean>(() => isClean || !!validationMessage, [isClean, validationMessage])

  // Automatically open the editor
  const [isEditing, setIsEditing] = useState<boolean>(shouldOpen)

  // Handlers for the toolbar
  const onRemove = useCallback(() => {
    onCommit(fieldId, isNumber ? null : '')
    setIsUnsaved(false)
  }, [fieldId, isNumber, onCommit])
  const onCancel = useCallback(() => {
    // For draft fields (newly added but not saved), remove them on cancel
    if (isDraft) {
      onRemove()
      return
    }
    // For existing fields, just reset to initial value and close
    setInputValue(initialValue)
    setIsEditing(false)
    setIsClean(true)
    setIsUnsaved(false) // Cancel explicitly discards changes
  }, [initialValue, isDraft, onRemove])
  const onUpdate = useCallback(() => {
    onCommit(fieldId, inputValue)
    setIsEditing(false)
    setIsClean(true)
    setIsUnsaved(false)
  }, [fieldId, onCommit, inputValue])

  // Handlers for the overlay
  const onOpen = useCallback(() => {
    // Ensure the editor starts from the latest committed value.
    if (isClean) setInputValue(initialValue)
    setIsEditing(true)
  }, [initialValue, isClean])
  const onClose = useCallback(() => {
    onClosed?.()
    if (isDraft) {
      onRemove()
      return
    }
    if (!isClean) {
      setIsUnsaved(true)
    }
    setIsEditing(false)
  }, [isClean, isDraft, onClosed, onRemove])

  // Keyboard commands
  const commandProps = useMemo(
    () => ({
      commands: {
        'github:cancel': onCancel,
        'github:submit-form': preventSubmit ? undefined : onUpdate,
      },
      keys: {
        'github:cancel': ['Escape'],
        'github:submit-form': ['Enter'],
      },
    }),
    [preventSubmit, onCancel, onUpdate],
  )

  // Overlay styles to match header
  const headerRef = useRef<HTMLButtonElement>(null)
  const [calculatedWidth, setCalculatedWidth] = useState(FALLBACK_WIDTH)

  // Calculate width when overlay opens
  useEffect(() => {
    if (isEditing && headerRef.current) {
      const width = headerRef.current.getBoundingClientRect().width
      if (width > 0) {
        setCalculatedWidth(`${width}px`)
      }
    }
  }, [isEditing])

  // Calculate the display title for the field
  const displayTitle = useMemo(() => {
    return isUnsaved ? `${fieldName} (edited)` : fieldName
  }, [fieldName, isUnsaved])

  return (
    <AnchoredOverlay
      open={isEditing}
      anchorRef={headerRef}
      renderAnchor={props => renderAnchor(props, headerRef, displayTitle)}
      onClose={onClose}
      onOpen={onOpen}
      overlayProps={{style: {width: overrideWidth ?? calculatedWidth}}}
    >
      <ScopedCommands {...commandProps}>
        <div className={textStyles.issuesFieldTextInputContainer}>
          <FormControl>
            <FormControl.Label visuallyHidden>{LABELS.textEditorLabel(fieldName)}</FormControl.Label>
            <TextInput
              block
              value={inputValue}
              placeholder={LABELS.textEditorPlaceholder}
              onChange={handleTextChange}
              {...inputProps}
            />
            {validationMessage && (
              <FormControl.Validation variant="error" className={textStyles.issuesFieldTextError}>
                {validationMessage}
              </FormControl.Validation>
            )}
          </FormControl>
        </div>
        <div
          className={textStyles.issuesFieldToolbar}
          role="toolbar"
          aria-label={LABELS.textEditorToolbarLabel(fieldName)}
        >
          <div className={textStyles.issuesFieldToolbarActions}>
            <Button
              size="small"
              onClick={onCancel}
              className={textStyles.actionButton}
              trailingVisual={<CommandKeybindingHint commandId="github:cancel" />}
            >
              {LABELS.textEditorCancelButton}
            </Button>
            <Button
              size="small"
              variant="primary"
              onClick={preventSubmit ? undefined : onUpdate}
              aria-disabled={preventSubmit}
              inactive={preventSubmit}
              aria-label={LABELS.textEditorUpdateButtonAriaLabel(fieldName)}
              className={textStyles.actionButton}
              trailingVisual={<CommandKeybindingHint commandId="github:submit-form" />}
            >
              {LABELS.textEditorUpdateButton}
            </Button>
          </div>
        </div>
      </ScopedCommands>
    </AnchoredOverlay>
  )
}

try{ IssueFieldTextEditor.displayName ||= 'IssueFieldTextEditor' } catch {}
try{ IssueFieldCompactTextEditor.displayName ||= 'IssueFieldCompactTextEditor' } catch {}
try{ IssueFieldTextEditorInternal.displayName ||= 'IssueFieldTextEditorInternal' } catch {}